import React, { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

import type { SystemTool, ToolConfig } from 'data/hooks/agents/types'
import { getUniqueApiName } from 'features/Agents/configuration/common/utils'

import useDebounce from 'v2/ui/utils/useDebounce'

import { Box } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import { Field } from 'ui/components/Field'
import { Input } from 'ui/components/Input'
import { Textarea } from 'ui/components/Textarea'
import { useToast } from 'ui/components/Toast'

import { ConfigureBulkUpsertRecordsTool } from './ConfigureBulkUpsertRecordsTool'
import { ConfigureDataAnalysisTool } from './ConfigureDataAnalysisTool'
import { ConfigureQueryRecordsTool } from './ConfigureQueryRecordsTool'
import { ConfigureRunWorkflowTool } from './ConfigureRunWorkflowTool'
import { ConfigureSendEmailTool } from './ConfigureSendEmailTool'
import { ConfigureToolFormInputs } from './ConfigureToolFormInputs'
import { GenericToolWithTableSelection } from './GenericToolWithTableSelection'
import { ToolListItem } from './ToolListItem'

const TOOL_CONFIGURE_COMPONENTS = {
    run_workflow: ConfigureRunWorkflowTool,
    data_analysis_tool: ConfigureDataAnalysisTool,
    query_records: ConfigureQueryRecordsTool,
    bulk_upsert_records: ConfigureBulkUpsertRecordsTool,
    send_email: ConfigureSendEmailTool,
    create_record: GenericToolWithTableSelection,
    update_record: GenericToolWithTableSelection,
}

const SET_VALUE_DEBOUNCE_MS = 100

export function ConfigureToolForm({
    tool,
    agentSid,
    reservedApiNames = [],
}: {
    tool: SystemTool
    agentSid: string
    reservedApiNames?: string[]
}) {
    const {
        register,
        watch,
        setValue,
        formState: { errors },
    } = useFormContext<ToolConfig>()

    const toast = useToast()

    const name = watch('name')
    const apiName = watch('api_name')

    const debouncedSetValue = useDebounce(setValue, SET_VALUE_DEBOUNCE_MS, [setValue], {
        leading: true,
    })

    useEffect(() => {
        if (!name) return

        const newApiName = getUniqueApiName(name, reservedApiNames)
        debouncedSetValue('api_name', newApiName, { shouldDirty: true })
    }, [name, debouncedSetValue, reservedApiNames])

    function copyApiName() {
        if (!apiName) return
        navigator.clipboard.writeText(apiName)
        toast({ title: 'Id copied to clipboard', type: 'success' })
    }

    const ConfigureComponent =
        TOOL_CONFIGURE_COMPONENTS[tool.api_name as keyof typeof TOOL_CONFIGURE_COMPONENTS]

    return (
        <Box flex column gap="l">
            <ToolListItem tool={tool} />
            <Field label="Name" width="5xl" shrink>
                <Input
                    placeholder="Enter tool name"
                    {...register('name', { required: 'Name is required' })}
                    isError={!!errors.name}
                    helperText={errors.name?.message}
                />
            </Field>
            <Field
                label="Tool Id"
                width="5xl"
                shrink
                rightSlotContent={
                    <Button
                        variant="ghost"
                        size="xs"
                        onClick={copyApiName}
                        iconOnly
                        startIcon={{ name: 'Copy' }}
                    />
                }
            >
                <Input readOnly {...register('api_name')} />
            </Field>

            <Field label="Description" width="5xl" shrink>
                <Textarea placeholder="Enter tool description" {...register('description')} />
            </Field>

            {ConfigureComponent ? (
                <ConfigureComponent tool={tool} agentSid={agentSid} />
            ) : (
                <ConfigureToolFormInputs inputs={tool.inputs} />
            )}

            <Button variant="primary" type="submit">
                Save Configuration
            </Button>
        </Box>
    )
}
